import type { ApolloError } from "@apollo/client"
import type { ReactNode } from "react"
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { globalHistory } from "@reach/router"

import { ProviderErrors, SEARCH_PAGE_SIZE } from "~config/constants"
import type {
    PublicSearchListingFieldsFragment,
    SearchListingsForSportsmenInput,
} from "~graphql/generated/graphql"
import { useSearchListings } from "~utils/use-listings"
import { trackSearch } from "~utils/event-tracking/event-tracking-helpers"

interface SearchContextType {
    listings: PublicSearchListingFieldsFragment[]
    isLoading: boolean
    total: number
    error?: ApolloError
    onPageChange: (page: number) => void
    currentPage: number
    searchInput: SearchListingsForSportsmenInput
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

interface SearchProviderProps {
    readonly searchInput: SearchListingsForSportsmenInput
    readonly children: ReactNode
}

export function SearchProvider({ searchInput, children }: SearchProviderProps) {
    const [currentPage, setCurrentPage] = useState(1)
    useTrackSearch(searchInput.text)

    const { listings, total, isLoading, error } = useSearchListings({
        searchParams: searchInput,
        options: {
            from: (currentPage - 1) * SEARCH_PAGE_SIZE,
            size: SEARCH_PAGE_SIZE,
        },
        fetchPolicy: "network-only",
    })

    const onPageChange = (page: number) => {
        setCurrentPage(page)
        window.scrollTo(0, 0)
    }

    useEffect(() => {
        // go to 1st result page on route change aka when search query changes
        globalHistory.listen(({ action }) => {
            if (action === "PUSH") {
                setCurrentPage(1)
            }
        })
    }, [])

    const value = useMemo(
        () => ({
            listings,
            total,
            isLoading,
            error,
            currentPage,
            onPageChange,
            searchInput,
        }),
        [
            listings,
            total,
            isLoading,
            error,
            currentPage,
            onPageChange,
            searchInput,
        ]
    )

    return (
        <SearchContext.Provider value={value}>
            {children}
        </SearchContext.Provider>
    )
}

export function useSearch() {
    const context = useContext(SearchContext)

    if (context === undefined) {
        throw new Error(ProviderErrors.Search)
    }

    return context
}

function useTrackSearch(query?: string | null | (string | null)[]) {
    useEffect(() => {
        if (!query) return
        trackSearch(query)
    }, [query])
}
